import type { RouteLocationRaw } from "vue-router"
import { createRouter, createWebHistory } from "vue-router"
import { useSessionStore } from "@/entities/session"
import { firstAllowedPath, navGroups } from "@/widgets/app-sidebar"

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/pages/login/ui/LoginPage.vue"),
      meta: { public: true, titleKey: "auth.title" },
    },
    {
      path: "/",
      component: () => import("@/app/layouts/ui/DefaultLayout.vue"),
      children: [
        {
          path: "",
          name: "home",
          component: () => import("@/pages/home/ui/HomePage.vue"),
          meta: { titleKey: "nav.home", permission: "dashboard.view" },
        },
        {
          path: "users",
          name: "users",
          component: () => import("@/pages/users/ui/UsersPage.vue"),
          meta: { titleKey: "nav.users", permission: "users.view" },
        },
        {
          path: "parents",
          name: "parents",
          component: () => import("@/pages/parents/ui/ParentsPage.vue"),
          meta: { titleKey: "nav.parents", permission: "users.view" },
        },
        {
          path: "parents/:userId",
          name: "parent-detail",
          component: () => import("@/pages/parent-detail/ui/ParentDetailPage.vue"),
          meta: { titleKey: "nav.parents", permission: "users.view" },
        },
        {
          path: "children",
          name: "children",
          component: () => import("@/pages/children/ui/ChildrenPage.vue"),
          meta: { titleKey: "nav.children", permission: "users.view" },
        },
        {
          path: "children/:childId",
          name: "child-detail",
          component: () => import("@/pages/child-detail/ui/ChildDetailPage.vue"),
          meta: { titleKey: "nav.children", permission: "users.view" },
        },
        {
          path: "children/:childId/devices/:deviceId",
          name: "device-detail",
          component: () => import("@/pages/device-detail/ui/DeviceDetailPage.vue"),
          meta: { titleKey: "nav.children", permission: "devices.view" },
        },
        {
          path: "subscriptions",
          name: "subscriptions",
          component: () => import("@/pages/subscriptions/ui/SubscriptionsPage.vue"),
          meta: { titleKey: "nav.subscriptions", permission: "subscriptions.view" },
        },
        {
          path: "subscriptions/:subscriptionId",
          name: "subscription-detail",
          component: () => import("@/pages/subscription-detail/ui/SubscriptionDetailPage.vue"),
          meta: { titleKey: "nav.subscriptions", permission: "subscriptions.view" },
        },
        {
          path: "app-versions",
          name: "app-versions",
          component: () => import("@/pages/app-versions/ui/AppVersionsPage.vue"),
          meta: { titleKey: "nav.appVersions", permission: "appversions.view" },
        },
        {
          path: "device-guides",
          name: "device-guides",
          component: () => import("@/pages/device-guides/ui/DeviceGuidesPage.vue"),
          meta: { titleKey: "nav.deviceGuides", permission: "deviceguides.view" },
        },
        {
          path: "roles",
          name: "roles",
          component: () => import("@/pages/roles/ui/RolesPage.vue"),
          meta: { titleKey: "nav.roles", permission: "roles.view" },
        },
        {
          path: "settings",
          name: "settings",
          component: () => import("@/pages/settings/ui/SettingsPage.vue"),
          meta: { titleKey: "account.settings" },
        },
      ],
    },
  ],
})

// Every section is gated, so an admin with a narrow role may not be allowed on
// the dashboard either. Land them on the first section their role does open, and
// fall back to settings, the one page every admin can reach.
function landingRoute(can: (code: string) => boolean): RouteLocationRaw {
  return firstAllowedPath(navGroups, can) ?? { name: "settings" }
}

// `meta.permission` holds a code from GET /api/Roles/Permissions, checked against
// the permissions the API attached to the signed-in admin.
router.beforeEach((to) => {
  const session = useSessionStore()

  if (!session.isAuthenticated)
    return to.meta.public ? true : { name: "login", query: { redirect: to.fullPath } }

  if (to.name === "login")
    return landingRoute(session.can)

  if (to.meta.permission && !session.can(to.meta.permission))
    return landingRoute(session.can)

  return true
})
